import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { showSuccessToast } from '@ui';
import { useDirectUpload } from '@hooks/useDirectUpload';
import { attachmentsKeys } from './queryKeys';
import { fileTypeLabel, formatFileSize, splitAttachmentName } from '../utils/fileFormatting';
/**
 * Bridges `useDirectUpload` (transport-agnostic, S3/contract-aware) to `AddAttachmentsBrushaway`'s
 * generic queue shape (storage-agnostic). Owns the hook instance and the drawer's own `isOpen`
 * state — both live here, one level above the organism, because queue state must survive the
 * drawer closing while a batch keeps running in the background (E2E-19).
 */
export function useAttachmentsUpload({ entityKey, ownerId, transport, defaultAttributes, toFields, t, successToast }) {
    const queryClient = useQueryClient();
    const upload = useDirectUpload({ transport, defaultAttributes });
    const [isOpen, setIsOpen] = useState(false);
    // E2E-07: a batch that finishes with every item `'done'` closes the drawer, shows the success
    // toast, and refreshes the table -- distinct from the background-progress toast, which reacts to
    // the drawer already being closed mid-upload, not to a clean foreground completion.
    // No dependency array: this must re-check the running->stopped transition after every render
    // (including ones triggered by `upload.items` progress ticks), not just when a memoized
    // dependency identity changes.
    const wasRunningRef = useRef(false);
    useEffect(() => {
        if (wasRunningRef.current && !upload.isRunning) {
            const allDone = upload.items.length > 0 && upload.items.every((item) => item.status === 'done');
            if (allDone) {
                setIsOpen(false);
                showSuccessToast(successToast(upload.items.length));
                void queryClient.invalidateQueries({ queryKey: attachmentsKeys(entityKey).list(ownerId) });
                upload.reset();
            }
        }
        wasRunningRef.current = upload.isRunning;
    });
    const items = useMemo(() => upload.items.map((item) => {
        const { basename: originalName, extension } = splitAttachmentName(item.file.name);
        return {
            id: item.id,
            originalName,
            name: item.name,
            extension,
            typeLabel: fileTypeLabel(item.file.name, t),
            sizeLabel: formatFileSize(item.file.size),
            byteSize: item.file.size,
            status: item.status,
            progress: item.progress,
            fields: toFields(item)
        };
    }), [upload.items, t, toFields]);
    const openDrawer = useCallback(() => setIsOpen(true), []);
    const onClose = useCallback(() => {
        setIsOpen(false);
        upload.reset();
    }, [upload]);
    const onCloseToBackground = useCallback(() => setIsOpen(false), []);
    const onFilesSelected = useCallback((files) => upload.addFiles(files), [upload]);
    const onNameChange = useCallback((id, nextBasename) => upload.updateItemMetadata(id, { name: nextBasename }), [upload]);
    const onRemoveItem = useCallback((id) => upload.removeItem(id), [upload]);
    const onRetryItem = useCallback((id) => {
        void upload.retry(id);
    }, [upload]);
    const onRetryFailed = useCallback(() => {
        void upload.retryFailed();
    }, [upload]);
    const onCancelUpload = useCallback(() => upload.cancel(), [upload]);
    const onSubmit = useCallback(() => {
        void upload.start();
    }, [upload]);
    return {
        isOpen,
        openDrawer,
        items,
        isUploading: upload.isRunning,
        aggregateProgress: upload.aggregateProgress,
        onFilesSelected,
        onNameChange,
        updateItemMetadata: upload.updateItemMetadata,
        onRemoveItem,
        onRetryItem,
        onRetryFailed,
        onCancelUpload,
        onSubmit,
        onClose,
        onCloseToBackground
    };
}
